import { Injectable, Injector } from '@angular/core';
import { DeviceService } from './device.service';
import { UploadService } from './upload.service';
import { ZoomService } from './zoom.service';
import { CommandEventType, CommandEvent, CommandChannelResponse } from './zoom.service';
import { Participant } from '@zoom/videosdk';
import { IUploadUrl } from './upload.service';
import { DownloadService, IDownloadUrl } from './download.service';
import { environment } from '../../environments/environment';
import { firstValueFrom, BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { SessionService } from './session.service';
import { MediaItem } from '../components/media-gallery/media-gallery.component';

interface ImageSettingsInterface {
  quality: number,
  height: number,
  width: number,
  thumbnailHeight: number
}

interface ICaptureIspectorImageResponse {
  blob: Blob,
  imageObjectUrl: URL
}

@Injectable({ 
  providedIn: 'root' 
})
export class MediaService {
  private _coordinatorVideoElement!: HTMLVideoElement;
  private _inspectorVideoElement!: HTMLVideoElement;
  private _inspectorLocalVideoElement!: HTMLVideoElement;
  private IMAGE_SETTINGS!: ImageSettingsInterface;
  private THUMBNAIL_SETTINGS!: ImageSettingsInterface;
  private zoomService!: ZoomService;
  private uploadService!: UploadService;
  private downloadService!: DownloadService;
  private _localVideoStream: MediaStream | null = null;
  private mediaSubject = new BehaviorSubject<MediaItem[]>([]);
  public media$ = this.mediaSubject.asObservable();

  apiUrl = `${environment.apiUrl}/media`;

  constructor(
    private deviceService: DeviceService,
    private injector: Injector,
    private http: HttpClient,
    private sessionService: SessionService,
  ) { 
    
    setTimeout(() => {
      this.zoomService = this.injector.get(ZoomService);
      this.uploadService = this.injector.get(UploadService);
      this.downloadService = this.injector.get(DownloadService);
      
      // Make debug methods available in console
      (window as any).debugMediaService = {
        refreshMedia: (sessionId: string) => this.refreshSessionMedia(sessionId),
        getMediaCount: () => this.mediaSubject.getValue().length,
        getCurrentMedia: () => this.mediaSubject.getValue(),
        clearMedia: () => this.mediaSubject.next([])
      };
      console.log('🔧 Debug methods available: window.debugMediaService');
    });

    this.IMAGE_SETTINGS = {
      quality: 0.85,
      height: this.imageHeight(),
      width: this.imageWidth(),
      thumbnailHeight: 78
    }
    
    this.THUMBNAIL_SETTINGS = {
      quality: 0.5,
      height: 96,
      width: 128,
      thumbnailHeight: 96
    }
  }

  get coordinatorVideoElement(): HTMLVideoElement | null {
    return this._coordinatorVideoElement;
  }

  set coordinatorVideoElement(videoElement: HTMLVideoElement) {
    this._coordinatorVideoElement = videoElement;
  }

  set inspectorVideoElement(videoElement: HTMLVideoElement) {
    this._inspectorVideoElement = videoElement;
  }

  get inspectorVideoElement(): HTMLVideoElement | null {
    return this._inspectorVideoElement;
  }

  set inspectorLocalVideoElement(videoElement: HTMLVideoElement) {
    this._inspectorLocalVideoElement = videoElement;
  }

  get inspectorLocalVideoElement(): HTMLVideoElement | null {
    return this._inspectorLocalVideoElement;
  }

  set localVideoStream(stream: MediaStream | null) {
    this._localVideoStream = stream;
  }

  get localVideoStream(): MediaStream | null {
    return this._localVideoStream;
  }

  getImageFilename(sessionId: string): string {
    const timestamp = new Date().getTime();
    return `gv_photo_${sessionId}_${timestamp}.jpg`;
  }
  async capturePhoto(sessionId: string): Promise<any|void> {
    console.log('🔴 Starting photo capture for session:', sessionId);
    try {
      // Validate the local video element exists (required for high-res native camera capture)
      if (!this._inspectorLocalVideoElement) {
        console.error('❌ No inspector local video element found');
        return;
      }

      // Use high-resolution native camera capture for ALL devices
      // This stops the Zoom stream, captures a 4K frame from native camera, then restarts
      const canvas = await this.computeHighResCapture(this._inspectorLocalVideoElement);

      const imageFilename = this.getImageFilename(sessionId);
      const contentType = 'image/jpeg';
      const image: ICaptureIspectorImageResponse = await new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          try {
            if (blob) {
              const imageObjectUrl = new URL(URL.createObjectURL(blob)); 
              resolve({ imageObjectUrl, blob } as ICaptureIspectorImageResponse);
            } else {
              console.error("Capture inspector image failed to convert canvas to blob");
              reject();
            }
          } catch (error: any) {
            console.error("Capture inspector image failed to convert canvas to blob", error);
            reject();
          }
        }, contentType, this.IMAGE_SETTINGS.quality);
      });
  
      if (image) {
        console.log('📸 Starting photo upload process...', { imageFilename, sessionId });
        
        const uploadResponse = await this.uploadService.uploadPhoto(
          imageFilename,
          sessionId,
          image.blob
        );
        
        if (uploadResponse) {
          console.log('✅ Photo uploaded to S3 successfully:', uploadResponse.key);
          
          // Retry logic for saving to database (common failure point)
          let mediaFile: MediaItem | null = null;
          let retryCount = 0;
          const maxRetries = 3;
          
          while (retryCount < maxRetries && !mediaFile) {
            try {
              console.log(`🔄 Attempting to save media to database (attempt ${retryCount + 1}/${maxRetries})`);
              mediaFile = await this.saveMedia(uploadResponse.key, sessionId);
              
              if (mediaFile) {
                console.log('✅ Photo saved to database successfully:', mediaFile.id);
                this.addMedia(mediaFile);
                console.log('✅ Photo added to gallery successfully');
                return mediaFile;
              } else {
                console.warn(`⚠️ Attempt ${retryCount + 1} failed - mediaFile is null/undefined`);
              }
            } catch (saveError: any) {
              console.error(`❌ Error in saveMedia process (attempt ${retryCount + 1}):`, saveError);
              if (retryCount === maxRetries - 1) {
                console.error('❌ All retry attempts failed. Stack trace:', saveError.stack);
              }
            }
            
            retryCount++;
            
            // Wait before retrying (exponential backoff)
            if (retryCount < maxRetries) {
              const waitTime = Math.pow(2, retryCount) * 1000; // 2s, 4s, 8s
              console.log(`⏳ Waiting ${waitTime}ms before retry...`);
              await new Promise(resolve => setTimeout(resolve, waitTime));
            }
          }
          
          console.error('❌ All attempts to save media failed');
          return null;
        } else {
          console.error('❌ Photo upload to S3 failed - no response');
        }
      } else {
        console.error('❌ No image blob created from canvas');
        return;
      }
          
    } catch (error: any) {
      console.error('Error capturing inspector image', error);
      throw new Error('Error capturing inspector image from canvas to blob', error);
    }
  }

  /**
   * Upload a generic media file (e.g., location map image)
   */
  async uploadMediaFile(file: File | Blob, sessionId: string, filename: string, label?: string): Promise<any> {
    try {
      console.log('🔴 Starting media file upload:', { filename, sessionId, label });
      
      // Upload to S3
      const uploadResponse = await this.uploadService.uploadPhoto(
        filename,
        sessionId,
        file instanceof File ? file : file
      );
      
      if (uploadResponse) {
        console.log('✅ Media file uploaded to S3 successfully:', uploadResponse.key);
        
        // Save to database
        let mediaFile: MediaItem | null = null;
        let retryCount = 0;
        const maxRetries = 3;
        
        while (retryCount < maxRetries && !mediaFile) {
          try {
            console.log(`🔄 Attempting to save media to database (attempt ${retryCount + 1}/${maxRetries})`);
            
            // Get object details and save with label
            const object: IDownloadUrl | void = await this.downloadService.getOjectDetails(uploadResponse.key);
            if (object) {
              const payload = {
                sessionId,
                name: object.key.split('/').pop(),
                key: uploadResponse.key,
                type: object.type,
                size: object.size,
                url: object.url,
                label: label || undefined
              };
              
              const response = await firstValueFrom(
                this.http.post(`${this.apiUrl}`, payload)
              );
              
              if (response) {
                mediaFile = response as MediaItem;
                console.log('✅ Media file saved to database successfully:', mediaFile.id);
                this.addMedia(mediaFile);
                console.log('✅ Media file added to gallery successfully');
                return mediaFile;
              }
            }
          } catch (saveError: any) {
            console.error(`❌ Error in saveMedia process (attempt ${retryCount + 1}):`, saveError);
            if (retryCount === maxRetries - 1) {
              console.error('❌ All retry attempts failed. Stack trace:', saveError.stack);
            }
          }
          
          retryCount++;
          
          // Wait before retrying (exponential backoff)
          if (retryCount < maxRetries) {
            const waitTime = Math.pow(2, retryCount) * 1000;
            console.log(`⏳ Waiting ${waitTime}ms before retry...`);
            await new Promise(resolve => setTimeout(resolve, waitTime));
          }
        }
        
        console.error('❌ All attempts to save media failed');
        return null;
      } else {
        console.error('❌ Media file upload to S3 failed - no response');
        return null;
      }
    } catch (error: any) {
      console.error('Error uploading media file', error);
      throw new Error('Error uploading media file: ' + error.message);
    }
  }

  imageHeight() : number {
      return this.deviceService.isMobile() ? 1080 : 1440;
  }

  imageWidth() : number {
      return this.deviceService.isMobile() ? 1440 : 1920;
  }

  async saveMedia(key: string, sessionId: string): Promise<any> {
    try {
      console.log('🔍 Getting object details for key:', key);
      
      let object: IDownloadUrl | void = await this.downloadService.getOjectDetails(key);
      if (object) {
        console.log('✅ Object details retrieved:', {
          key: object.key,
          type: object.type,
          size: object.size,
          url: object.url ? `${object.url.substring(0, 50)}...` : 'No URL'
        });
        
        const payload = {
          sessionId,
          name: object.key.split('/').pop(),
          key,
          type: object.type,
          size: object.size,
          url: object.url
        };
        
        console.log('📡 Sending POST request to save media:', {
          endpoint: this.apiUrl,
          payload: { ...payload, url: payload.url ? `${payload.url.substring(0, 50)}...` : 'No URL' }
        });
        
        const response = await firstValueFrom(
          this.http.post(`${this.apiUrl}`, payload)
        );
        
        if (response) {
          console.log('✅ Media saved to database successfully:', response);
          return response;
        } else {
          console.error('❌ Database save returned empty response');
        }
      } else {
        console.error('❌ Failed to get object details for key:', key);
      }
    } catch (error: any) {
      console.error('❌ Error saving media:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        error: error.error
      });
      throw error; // Re-throw the original error instead of wrapping it
    }
    console.error('❌ saveMedia returning null - no object found or response empty');
    return null;
  }

  stopLocalMedia() {
    if (this._localVideoStream) {
      this._localVideoStream.getTracks().forEach((track) => {
        track.stop();
      });
    }
  } // Calculate the final video size

  /**
   * Captures a high-resolution photo by temporarily stopping the Zoom stream,
   * accessing the native camera directly via getUserMedia for 4K capture,
   * then resuming the Zoom stream.
   *
   * Works on all platforms (iOS, Android, desktop).
   */
  private async computeHighResCapture(
    videoElement: HTMLVideoElement
  ): Promise<HTMLCanvasElement> {
    return new Promise(async (resolve) => {
      const stream = (await this.zoomService.client?.getMediaStream()) as any;

      // Fade out the video to prevent flash
      videoElement.style.opacity = '0';

      // Capture current frame as placeholder
      const canvas = document.createElement('canvas');
      canvas.classList.add('placeholder-canvas');
      canvas.width = videoElement.videoWidth || videoElement.clientWidth;
      canvas.height = videoElement.videoHeight || videoElement.clientHeight;

      const ctx = canvas.getContext('2d');
      ctx!.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

      videoElement.parentNode!.insertBefore(canvas, videoElement);
      canvas.style.display = 'block';

      // Need to stop Zoom stream because camera can't record and stream at the same time
      await stream.stopVideo();

      console.log('📸 Requesting high-resolution camera access...');
      const isMobile = this.deviceService.isMobile();
      const localStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { exact: 'environment' }, // Back camera
          width: { ideal: isMobile ? 1440 : 1920 },
          height: { ideal: isMobile ? 1080 : 1440 },
        },
      });

      videoElement.srcObject = localStream;

      videoElement.muted = true;
      videoElement.playsInline = true;

      // Play the video to initialize the stream
      await videoElement.play();

      // Update canvas size if necessary
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;

      console.log('📸 Captured high-res frame. Dimensions:', canvas.width, 'x', canvas.height);

      ctx!.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

      videoElement.srcObject = null;

      // Stop the local stream
      localStream.getTracks().forEach((track) => track.stop());

      // Fade back in and remove the placeholder
      videoElement.style.opacity = '1';
      canvas.style.display = 'none';

      // Clean up placeholder
      videoElement.parentNode!.removeChild(canvas);

      // Downsample if the camera delivered a higher resolution than IMAGE_SETTINGS
      const targetWidth = this.IMAGE_SETTINGS.width;
      const targetHeight = this.IMAGE_SETTINGS.height;
      if (canvas.width > targetWidth || canvas.height > targetHeight) {
        console.log('📸 Downsampling from', canvas.width, 'x', canvas.height, 'to', targetWidth, 'x', targetHeight);
        const resized = document.createElement('canvas');
        resized.width = targetWidth;
        resized.height = targetHeight;
        resized.getContext('2d')!.drawImage(canvas, 0, 0, targetWidth, targetHeight);
        resolve(resized);
        return;
      }

      resolve(canvas);

      // Resume Zoom stream
      await stream.startVideo();
    });
  }

  private isAppleDevice(): boolean {
    return /iPhone|iPad|iPod|Macintosh/i.test(navigator.userAgent);
  }

  // Add a new media item to the list
  addMedia(media: MediaItem) {
    console.log('🖼️ Adding media to gallery:', {
      id: media.id,
      name: media.name,
      key: media.key,
      url: media.url ? `${media.url.substring(0, 50)}...` : 'No URL'
    });
    
    const currentMedia = this.mediaSubject.getValue();
    const newMedia = { ...media, sortOrder: 0 }; // Add to beginning
    const newMediaArray = [newMedia, ...currentMedia];
    
    console.log('🖼️ Updated media array length:', newMediaArray.length);
    
    // Emit the new media array immediately
    this.mediaSubject.next(newMediaArray);
    
    console.log('✅ Media successfully added to BehaviorSubject');
  }

  // Get all media for a session
  async getSessionMedia(sessionId: string): Promise<MediaItem[]> {
    try {
      console.log('🔄 Fetching session media from API for session:', sessionId);
      const response = await firstValueFrom(
        this.http.get<any>(`${this.apiUrl}/session/${sessionId}`)
      );
      if (response?.mediaFiles) {
        const mediaItems = response.mediaFiles.map((item: any) => ({
          ...item,
          createdAt: new Date(item.createdAt),
          selected: false
        }));
        console.log('✅ Fetched media items from API:', mediaItems.length);
        this.mediaSubject.next(mediaItems);
        return mediaItems;
      }
    } catch (error) {
      console.error('❌ Error fetching session media', error);
    }
    return [];
  }

  // Refresh media gallery from API (useful for recovering missing photos)
  async refreshSessionMedia(sessionId: string): Promise<MediaItem[]> {
    console.log('🔄 Refreshing media gallery from API...');
    const mediaItems = await this.getSessionMedia(sessionId);
    console.log(`✅ Refreshed media gallery: ${mediaItems.length} items`);
    return mediaItems;
  }

  // Get composition status for session
  async getSessionCompositions(sessionId: string): Promise<any> {
    try {
      const response = await firstValueFrom(
        this.http.get<any>(`${this.apiUrl}/session/${sessionId}/compositions`)
      );
      return response;
    } catch (error) {
      console.error('❌ Error fetching composition status:', error);
      return null;
    }
  }

  // Update media item
  async updateMedia(mediaId: string, updates: { label?: string; sortOrder?: number }): Promise<MediaItem | null> {
    try {
      const response = await firstValueFrom(
        this.http.patch<MediaItem>(`${this.apiUrl}/${mediaId}`, updates)
      );
      
      // Update local state
      const currentMedia = this.mediaSubject.getValue();
      const updatedMedia = currentMedia.map(item => 
        item.id === mediaId ? { ...item, ...updates } : item
      );
      this.mediaSubject.next(updatedMedia);
      
      return response;
    } catch (error) {
      console.error('Error updating media', error);
      return null;
    }
  }

  // Delete media item
  async deleteMedia(mediaId: string): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.delete(`${this.apiUrl}/${mediaId}`)
      );
      
      // Update local state
      const currentMedia = this.mediaSubject.getValue();
      const filteredMedia = currentMedia.filter(item => item.id !== mediaId);
      this.mediaSubject.next(filteredMedia);
      
      return true;
    } catch (error) {
      console.error('Error deleting media', error);
      return false;
    }
  }

  // Batch delete media items
  async batchDeleteMedia(mediaIds: string[]): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.http.delete(`${this.apiUrl}/batch`, {
          body: { mediaIds }
        })
      );
      
      // Update local state - remove deleted items
      const currentMedia = this.mediaSubject.getValue();
      const filteredMedia = currentMedia.filter(item => !mediaIds.includes(item.id));
      this.mediaSubject.next(filteredMedia);
      
      return true;
    } catch (error) {
      console.error('Error batch deleting media', error);
      return false;
    }
  }

  // Batch update media items
  async batchUpdateMedia(mediaIds: string[], updates: { label?: string }): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.http.put(`${this.apiUrl}/batch/update`, {
          mediaIds,
          ...updates
        })
      );
      
      // Update local state
      const currentMedia = this.mediaSubject.getValue();
      const updatedMedia = currentMedia.map(item => 
        mediaIds.includes(item.id) ? { ...item, ...updates } : item
      );
      this.mediaSubject.next(updatedMedia);
      
      return true;
    } catch (error) {
      console.error('Error batch updating media', error);
      return false;
    }
  }

  // Reorder media items
  async reorderMedia(sessionId: string, mediaIds: string[]): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.put(`${this.apiUrl}/session/${sessionId}/reorder`, {
          mediaIds
        })
      );
      
      // Update local state with new order
      const currentMedia = this.mediaSubject.getValue();
      const reorderedMedia = mediaIds.map((id, index) => {
        const item = currentMedia.find(media => media.id === id);
        return item ? { ...item, sortOrder: index } : null;
      }).filter(Boolean) as MediaItem[];
      
      // Add any items that weren't in the reorder list
      const otherItems = currentMedia.filter(item => !mediaIds.includes(item.id));
      const allMedia = [...reorderedMedia, ...otherItems];
      
      this.mediaSubject.next(allMedia);
      
      return true;
    } catch (error) {
      console.error('Error reordering media', error);
      return false;
    }
  }
}
